import styled from '@emotion/styled'
import { Avatar, Box, ButtonBase, Divider, ListItemText, MenuItem, Popover, Typography } from '@mui/material'
import Link from '@mui/material/Link'
import { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { useAuth } from 'torqcloud-ui'

const PopoverStyled = styled(Popover)`
  display: inline-flex;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  .util-menu {
    margin-top: 0;
    padding: 8px;

    a {
      padding: 4px 0;
    }
  }
  .secondary-link {
    margin-left: 8px;
  }

  .menu-link h6::after {
    content: url('img/icon/navigate_next.svg');
    width: 12px;
    height: 12px;
    top: 10px;
    padding-left: 4px;
    position: absolute;
  }

  .log-out-link h6::after {
    content: url('img/icon/logout.svg');
    width: 12px;
    height: 12px;
    top: 8px;
    position: absolute;
    padding-left: 10px;
  }
`

export interface AccountDropdownProps {
  userSettingsUrl: string
  organizationSettingsUrl?: string
  showOrganizationSettings?: boolean
}

export default function AccountDropdown({
  userSettingsUrl,
  organizationSettingsUrl,
  showOrganizationSettings = false,
}: AccountDropdownProps) {
  const anchorRef = useRef(null)
  const { logout, user } = useAuth()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleLogout = async () => {
    try {
      handleClose()
      await logout()
      navigate('/')
    } catch (err) {
      console.error(err)
      toast.error('Unable to logout.')
    }
  }

  return (
    <>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
        onClick={handleOpen}
        component={ButtonBase}
        ref={anchorRef}
      >
        <Box
          sx={{
            ml: 1,
          }}
        ></Box>

        <Box sx={{ textAlign: 'left' }}>
          <Typography color="textPrimary" variant="subtitle2">
            Hello, {user.name}!
          </Typography>
        </Box>
        <Avatar
          src={user.avatar}
          sx={{
            cursor: 'pointer',
            height: 40,
            width: 40,
            ml: 2,
          }}
        />
      </Box>
      <PopoverStyled
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            sx: { width: 240 },
          },
        }}
      >
        <Divider />
        <Box m={0} p={1}>
          <Link component={RouterLink} to={userSettingsUrl} sx={{ textDecoration: 'none' }}>
            <MenuItem className="menu-link">
              <ListItemText
                primary={
                  <Typography color="textPrimary" variant="subtitle2">
                    Settings
                  </Typography>
                }
              />
            </MenuItem>
          </Link>
          {showOrganizationSettings && organizationSettingsUrl && (
            <Link component={RouterLink} to={organizationSettingsUrl} sx={{ textDecoration: 'none' }}>
              <MenuItem className="menu-link">
                <ListItemText
                  primary={
                    <Typography color="textPrimary" variant="subtitle2">
                      Organization Settings
                    </Typography>
                  }
                />
              </MenuItem>
            </Link>
          )}
          <MenuItem className="menu-link log-out-link" onClick={handleLogout}>
            <ListItemText
              primary={
                <Typography color="textPrimary" variant="subtitle2">
                  Logout
                </Typography>
              }
            />
          </MenuItem>
        </Box>
      </PopoverStyled>
    </>
  )
}
